import { useState, useEffect } from "react";
import {
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
  Target,
  Zap,
  Brain,
  Gauge,
  ShieldCheck,
  Timer,
  MessageCircle,
  Linkedin,
  Youtube,
  Instagram,
  GraduationCap,
  Users,
  Flame,
  TrendingUp,
  Quote,
  ChevronRight,
  Play,
  Mail,
  MapPin,
  Phone,
  ExternalLink,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ApplicationForm from "@/components/ApplicationForm";


const MENTOR_PHOTO = "/images/mentor-photo.jpg";
const PROMO_BANNER = "/images/promo-banner.jpg";
const PROMO_POSTER = "/images/promo-poster.jpg";

const WA_NUMBER = "917448550397";
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
  "Hi Suraj sir, I'm interested in your JEE Maths Mentorship. Please guide me."
)}`;
const LINKEDIN_URL =
  "https://www.linkedin.com/in/suraj-kumar-singh-iit-jee-advanced-maths-mentor";
const YOUTUBE_URL = "https://www.youtube.com/@MATHSMAMASURAJSIR";
const INSTAGRAM_URL = "https://www.instagram.com/mathsmamaedtech/";
const WEBSITE_URL = "https://mathsmamasurajsir.com/";
const TOPMATE_URL = "https://topmate.io/suraj_kumar_singh_499/";
const EMAIL = "mathsmamaedtech@gmail.com";

const SOCIAL_LINKS = [
  { href: WA_LINK, label: "WhatsApp", icon: MessageCircle, testId: "footer-whatsapp" },
  { href: LINKEDIN_URL, label: "LinkedIn", icon: Linkedin, testId: "footer-linkedin" },
  { href: YOUTUBE_URL, label: "YouTube", icon: Youtube, testId: "footer-youtube" },
  { href: INSTAGRAM_URL, label: "Instagram", icon: Instagram, testId: "footer-instagram" },
];

const PAIN_POINTS = [
  { icon: XCircle, text: "Practice Randomly" },
  { icon: XCircle, text: "Memorize Instead of Understanding" },
  { icon: XCircle, text: "Panic During Difficult Questions" },
  { icon: XCircle, text: "Make Repeated Mistakes" },
  { icon: XCircle, text: "Lack Proper Guidance" },
  { icon: XCircle, text: "Never Learn Real Solving Systems" },
];

const DIFFERENTIATORS = [
  "How Rankers Think",
  "How To Decode Questions Faster",
  "How To Approach Problems Strategically",
  "How To Reduce Silly Mistakes",
  "How To Solve Under Pressure",
  "How To Build Real Exam Confidence",
];

const PILLARS = [
  {
    icon: Brain,
    title: "Concept Clarity Engineering",
    desc: "Build strong understanding instead of memorization.",
  },
  {
    icon: Gauge,
    title: "Speed Building Systems",
    desc: "Learn how to solve faster without losing accuracy.",
  },
  {
    icon: Target,
    title: "Pattern Recognition",
    desc: "Identify question types quickly during exams.",
  },
  {
    icon: Sparkles,
    title: "Advanced Problem Solving",
    desc: "Approach difficult problems systematically.",
  },
  {
    icon: ShieldCheck,
    title: "Accuracy Improvement",
    desc: "Remove repeated mistakes and careless errors.",
  },
  {
    icon: Flame,
    title: "Exam Psychology",
    desc: "Develop calmness and confidence under pressure.",
  },
];

const TRANSFORMATION = [
  ["Confused During Questions", "Clear Question Analysis"],
  ["Slow Solving Speed", "Faster Execution"],
  ["Random Practice", "Structured Strategy"],
  ["Low Confidence", "Strong Exam Confidence"],
  ["Repeated Mistakes", "Better Accuracy"],
  ["Fear of Maths", "Control Over Maths"],
];

const AUDIENCE = [
  { title: "Serious JEE Aspirants", desc: "Students committed to improving Maths performance." },
  { title: "Students Aged 16–21", desc: "Class 11, 12, and Droppers." },
  { title: "Aspirants Wanting Personal Guidance", desc: "Students tired of generic coaching systems." },
  { title: "Students Ready To Work Seriously", desc: "This is for action-takers, not passive learners." },
];

const TESTIMONIALS = [
  {
    quote: "For the first time, Maths started feeling manageable.",
    name: "Rishabh",
    role: "Class 12 Aspirant",
  },
  {
    quote: "I understood how to actually think during questions.",
    name: "Aarav",
    role: "Dropper",
  },
  {
    quote: "My speed and confidence improved together.",
    name: "Sneha",
    role: "Class 11",
  },
  {
    quote: "I stopped panicking during difficult problems.",
    name: "Kartik",
    role: "Class 12",
  },
  {
    quote: "The personal guidance changed my preparation.",
    name: "Ananya",
    role: "Dropper",
  },
];

const FAQS = [
  {
    q: "Is this 1:1 or group mentorship?",
    a: "Both options are available. Based on your fit call, Suraj sir recommends the track that matches your goals, budget and current level.",
  },
  {
    q: "Is this useful for droppers?",
    a: "Yes. The mentorship is especially designed for droppers wanting serious improvement in problem solving speed, accuracy and exam psychology.",
  },
  {
    q: "Will doubts be solved personally?",
    a: "Yes. 1:1 students get on-phone-call doubt clearing sessions. Group students get YouTube LIVE doubt sessions. Both tracks include WhatsApp support.",
  },
  {
    q: "Is this only for strong students?",
    a: "No. This is designed to improve mathematical thinking at every level — from students stuck in basics to those aiming for top ranks. Classes 9–12, JEE Mains/Advanced, and Foundation levels are all covered.",
  },
  {
    q: "Will classes be LIVE?",
    a: "Yes. All sessions are LIVE and fully interactive so you can ask questions in real time. We encourage two-way communication and doubts after every 10 minutes of teaching.",
  },
  {
    q: "Is this better than generic coaching?",
    a: "This mentorship focuses on personal improvement, strategic thinking and real execution — not just delivering theory like regular coaching.",
  },
  {
    q: "Can I book a free demo class first?",
    a: "Absolutely. Book your free demo with Suraj sir on Topmate or WhatsApp. You'll experience the storytelling-based teaching approach firsthand.",
  },
  {
    q: "What study material will I get?",
    a: "You get Kota's updated study material, daily quality DPPs, short notes for revision, topic-wise PYQs (JEE Mains & Advanced last 15 years), and separate test papers for mock practice.",
  },
];

// ---------- Reusable UI ----------
function CtaPrimary({ onClick, children, testId, className = "" }) {
  return (
    <Button
      onClick={onClick}
      data-testid={testId}
      className={
        "bg-[#FFD700] hover:bg-[#E6C200] text-black font-semibold uppercase tracking-wider h-12 px-7 rounded-md text-sm md:text-base transition-all hover:-translate-y-[2px] shadow-[0_10px_30px_-10px_rgba(255,215,0,0.5)] " +
        className
      }
    >
      {children}
      <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  );
}

function CtaGhost({ href, children, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-white/20 text-white text-sm md:text-base hover:bg-white/5 transition-colors uppercase tracking-wider font-medium"
    >
      <MessageCircle className="h-4 w-4" />
      {children}
    </a>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.28em] text-[#FFD700] font-semibold mb-5">
      <span className="inline-block w-6 h-[1px] bg-[#FFD700]" />
      {children}
    </div>
  );
}

function SocialLinks({ className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {SOCIAL_LINKS.map(({ href, label, icon: Icon, testId }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testId}
          aria-label={label}
          className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FFD700]/40 hover:bg-white/5 transition-colors"
        >
          <Icon className="h-4 w-4" />
        </a>
      ))}
    </div>
  );
}

// ---------- Main page ----------
export default function Landing() {
  const [formOpen, setFormOpen] = useState(false);
  const [source, setSource] = useState("hero");

  const openForm = (src = "cta") => {
    setSource(src);
    setFormOpen(true);
  };

  // Stop background scroll when dialog open already handled by Radix
  useEffect(() => {
    document.title =
      "Suraj Kumar Singh | JEE Maths 1:1 & Group Mentorship | Kota Expert Faculty";
  }, []);

  return (
    <div className="relative bg-[#0A0A0A] text-white min-h-screen overflow-x-hidden">
      {/* Urgency strip */}
      <div
        data-testid="urgency-strip"
        className="sticky top-0 z-[60] w-full bg-[#FF3B30] text-white text-center text-[11px] md:text-sm font-semibold py-2 px-4 tracking-wide"
      >
        <span className="inline-flex items-center gap-2 justify-center">
          <Timer className="h-3.5 w-3.5" />
          LIMITED MENTORSHIP SLOTS OPEN FOR SERIOUS JEE ASPIRANTS • ADMISSIONS CLOSING SOON
        </span>
      </div>

      {/* Top nav */}
      <header className="sticky top-[34px] md:top-[38px] z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-md bg-[#FFD700] flex items-center justify-center text-black font-display font-bold">
              S
            </div>
            <div className="leading-tight">
              <div className="font-display text-base tracking-tight">Suraj Kumar Singh</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                Kota Expert • JEE Maths
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-gray-300">
            <a href="#mentorship" className="hover:text-white">Mentorship</a>
            <a href="#learn" className="hover:text-white">Curriculum</a>
            <a href="#results" className="hover:text-white">Results</a>
            <a href="#youtube" className="hover:text-white">YouTube</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
            <SocialLinks />
          </nav>
          <Button
            onClick={() => openForm("nav")}
            data-testid="nav-apply-button"
            className="bg-[#FFD700] hover:bg-[#E6C200] text-black font-semibold uppercase tracking-wide h-10 px-5 text-xs md:text-sm"
          >
            Apply Now
          </Button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center noise overflow-hidden">
        <div className="absolute inset-0 hero-gradient">
          <div className="absolute inset-0 grid-pattern opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 w-full">
          <div className="max-w-4xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFD700]/30 bg-[#FFD700]/5 text-[#FFD700] text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-7"
              data-testid="hero-authority-badge"
            >
              <GraduationCap className="h-3.5 w-3.5" />
              Kota Expert Maths Faculty • 12+ Years
            </div>

            <h1
              className="font-display font-semibold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-[-0.03em]"
              data-testid="hero-headline"
            >
              Stop Feeling Lost in{" "}
              <span className="text-[#FFD700]">JEE Maths.</span>
              <br />
              Learn Directly From a{" "}
              <span className="relative inline-block">
                Kota Expert.
                <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#FFD700]/70" />
              </span>
            </h1>

            <p className="mt-7 text-base md:text-xl text-gray-300 max-w-2xl leading-relaxed">
              Personalized <span className="text-white font-medium">1:1 and Small Group</span>{" "}
              Maths mentorship for serious JEE aspirants who want faster solving, sharper
              concept clarity, higher accuracy and real exam confidence.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <CtaPrimary
                onClick={() => openForm("hero")}
                testId="hero-apply-button"
              >
                Apply for Mentorship
              </CtaPrimary>
              <CtaGhost href={WA_LINK} testId="hero-whatsapp-button">
                Talk on WhatsApp
              </CtaGhost>
            </div>

            <p className="mt-5 text-xs md:text-sm text-gray-500 uppercase tracking-[0.22em]">
              Limited Seats • Serious Aspirants Only • Personal Guidance Included
            </p>

            {/* Mini stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 max-w-3xl">
              {[
                { k: "12+", v: "Years Teaching" },
                { k: "1000+", v: "Students Mentored" },
                { k: "LIVE", v: "Interactive Classes" },
                { k: "1:1", v: "Personal Support" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="border border-white/10 bg-white/[0.02] rounded-md px-4 py-4 md:py-5"
                >
                  <div className="font-display text-2xl md:text-3xl text-[#FFD700] tracking-tight">
                    {s.k}
                  </div>
                  <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-gray-400 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Overlay promise ticker */}
      <section className="bg-[#FFD700] text-black py-4 overflow-hidden border-y border-black/10">
        <div className="ticker-track whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, g) => (
            <div key={g} className="flex items-center gap-10 px-6 text-sm md:text-base font-semibold uppercase tracking-[0.18em]">
              {[
                "Your Maths Improves When Your Thinking Improves",
                "Faster Solving",
                "Sharper Accuracy",
                "Higher Mock Scores",
                "Real Exam Confidence",
                "Ranker-Level Problem Solving",
                "Personal Attention",
              ].map((t, i) => (
                <span key={i} className="flex items-center gap-10">
                  <span>{t}</span>
                  <Sparkles className="h-4 w-4 shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* AUTHORITY */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-xl overflow-hidden border border-white/10">
              <img
                src={MENTOR_PHOTO}
                alt="Suraj Kumar Singh — JEE Maths mentor"
                className="w-full h-[420px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFD700] mb-1">
                  Mentor
                </div>
                <div className="font-display text-2xl md:text-3xl">Suraj Kumar Singh</div>
                <div className="text-sm text-gray-300">Kota Expert Maths Faculty</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionLabel>The Mentor</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
              12+ Years Of Teaching JEE Aspirants{" "}
              <span className="text-[#FFD700]">How To Actually Think In Maths.</span>
            </h2>
            <p className="mt-5 text-gray-300 text-base md:text-lg leading-relaxed max-w-2xl">
              Trained under legendary <span className="text-white font-medium">Mr. V.K. Bansal Sir</span> (Founder of Bansal Classes) in Kota.
              Suraj sir uses a unique <span className="text-white font-medium">Storytelling & Real-Life Analogy Approach</span> —
              turning vectors into cricket shots, calculus into traffic flows — making complex concepts unforgettable.
              Thousands of JEE Mains ranks and multiple double-digit JEE Advanced ranks.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Ex Bansal Classes Kota", "Ex Aakash", "Ex Sri Chaitanya S60 Faculty", "Trained Under V.K. Bansal Sir"].map((c) => (
                <span
                  key={c}
                  className="text-[10px] md:text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-[#FFD700]/25 bg-[#FFD700]/5 text-[#FFD700] font-medium"
                >
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Brain, t: "Mathematical Thinking" },
                { icon: Gauge, t: "Speed" },
                { icon: Target, t: "Accuracy" },
                { icon: TrendingUp, t: "Ranker Mindset" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="border border-white/10 rounded-lg p-5 hover:border-[#FFD700]/40 transition-colors"
                >
                  <s.icon className="h-5 w-5 text-[#FFD700]" />
                  <div className="mt-3 text-sm font-medium">{s.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM AMPLIFICATION */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>The Real Problem</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-4xl leading-[1.08]">
            You Are Not Failing Because You Lack Intelligence.
            <br />
            <span className="text-gray-500">
              You Are Failing Because Of{" "}
              <span className="text-[#FF3B30]">Strategy.</span>
            </span>
          </h2>
          <p className="mt-6 text-gray-300 max-w-3xl text-base md:text-lg">
            The problem is NOT hard work. Most JEE aspirants silently struggle with the same
            patterns — and never get diagnosed.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {PAIN_POINTS.map((p, i) => (
              <div
                key={i}
                className="group relative border border-white/10 rounded-xl p-6 md:p-7 bg-white/[0.02] hover:border-[#FF3B30]/40 hover:-translate-y-1 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
                    <p.icon className="h-4 w-4 text-[#FF3B30]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500">
                      Symptom #{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1 font-display text-xl tracking-tight">{p.text}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 border-t border-white/10 pt-10 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              "Confidence Drops",
              "Mock Scores Stay Stuck",
              "Maths Feels Overwhelming",
              "Speed Never Improves",
            ].map((t, i) => (
              <div key={i} className="text-gray-400">
                <div className="font-display text-3xl text-white">—</div>
                <div className="mt-2 text-sm uppercase tracking-widest">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel>Why This Is Different</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
              This Is <span className="line-through text-gray-600">Not</span> Another Online Class.
              <br />
              <span className="text-[#FFD700]">It's Personal Mentorship.</span>
            </h2>
            <p className="mt-5 text-gray-300 text-base md:text-lg max-w-xl">
              Most programs give long lectures, generic teaching and zero attention. Here, you
              learn the exact thinking systems used by ranker-level students.
            </p>
            <div className="mt-8">
              <CtaPrimary
                onClick={() => openForm("differentiator")}
                testId="differentiator-apply-button"
              >
                Claim Your Seat
              </CtaPrimary>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {DIFFERENTIATORS.map((d, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-lg p-5 bg-white/[0.02] hover:border-[#FFD700]/40 hover:-translate-y-1 transition-all"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#FFD700] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500">
                      Skill #{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1 font-display text-lg tracking-tight">{d}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MENTORSHIP OFFERS */}
      <section id="mentorship" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>Pick Your Track</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-3xl leading-[1.1]">
            1:1 Personal or High-Performance Small Group.
            <br />
            <span className="text-gray-500">Both Built For Serious Aspirants.</span>
          </h2>

          <div className="mt-10 max-w-3xl mx-auto rounded-2xl overflow-hidden border border-white/10 bg-[#141414]">
            <img
              src={PROMO_BANNER}
              alt="Maths Mama Edtech — 1:1 mentorship ₹999/hour, group classes ₹5000/month"
              className="w-full h-auto object-contain"
            />
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* 1:1 Card - premium */}
            <div
              className="relative rounded-2xl p-8 md:p-10 bg-[#141414] border border-[#FFD700]/40 glow-yellow"
              data-testid="offer-1on1-card"
            >
              <div className="absolute -top-3 left-8 bg-[#FFD700] text-black text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full">
                Premium • Most Personal
              </div>
              <Users className="h-8 w-8 text-[#FFD700]" />
              <h3 className="mt-5 font-display text-2xl md:text-3xl tracking-tight">
                1:1 Personal Mentorship
              </h3>
              <p className="mt-2 text-gray-400 text-sm md:text-base">
                Direct, private guidance — weak chapters, solving techniques, mocks, strategy.
              </p>
              <ul className="mt-6 space-y-3 text-sm md:text-base text-gray-200">
                {[
                  "Weak chapters diagnosed & fixed",
                  "Personal question-solving techniques",
                  "Time management & error analysis",
                  "Mock test performance review",
                  "Custom study planning",
                  "Doubt support on demand",
                ].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#FFD700] shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-3">
                  Perfect For
                </div>
                <div className="flex flex-wrap gap-2">
                  {["Droppers", "Stuck in Maths", "Need personal attention", "Want faster improvement"].map(
                    (t) => (
                      <span
                        key={t}
                        className="text-xs px-3 py-1 rounded-full border border-white/10 text-gray-300"
                      >
                        {t}
                      </span>
                    )
                  )}
                </div>
                <CtaPrimary
                  className="mt-7 w-full"
                  onClick={() => openForm("1:1")}
                  testId="offer-1on1-apply-button"
                >
                  Apply for 1:1 Mentorship
                </CtaPrimary>
              </div>
            </div>

            {/* Group Card */}
            <div
              className="relative rounded-2xl p-8 md:p-10 bg-[#141414] border border-white/10 hover:border-white/25 transition-colors"
              data-testid="offer-group-card"
            >
              <div className="absolute -top-3 left-8 bg-white text-black text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full">
                Competitive • High Performance
              </div>
              <Flame className="h-8 w-8 text-white" />
              <h3 className="mt-5 font-display text-2xl md:text-3xl tracking-tight">
                Small Group Mentorship
              </h3>
              <p className="mt-2 text-gray-400 text-sm md:text-base">
                A focused batch designed to push you with competitive peers & live execution.
              </p>
              <ul className="mt-6 space-y-3 text-sm md:text-base text-gray-200">
                {[
                  "Competitive peer environment",
                  "Faster learning curve",
                  "LIVE interactive sessions",
                  "Advanced problem solving drills",
                  "Ranker thinking patterns",
                  "Motivation + accountability",
                ].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-white/80 shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-3">
                  Perfect For
                </div>
                <div className="flex flex-wrap gap-2">
                  {["Class 11 / 12", "Want competitive env.", "Ready to work hard", "Love structure"].map(
                    (t) => (
                      <span
                        key={t}
                        className="text-xs px-3 py-1 rounded-full border border-white/10 text-gray-300"
                      >
                        {t}
                      </span>
                    )
                  )}
                </div>
                <Button
                  onClick={() => openForm("group")}
                  data-testid="offer-group-apply-button"
                  className="mt-7 w-full bg-white hover:bg-gray-200 text-black font-semibold uppercase tracking-wider h-12 text-sm md:text-base"
                >
                  Apply for Group Mentorship
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEARN PILLARS */}
      <section id="learn" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>Inside The Program</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-3xl leading-[1.1]">
            Six Pillars That Rewire How You{" "}
            <span className="text-[#FFD700]">Think & Solve.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group border border-white/10 rounded-xl p-7 bg-white/[0.02] hover:border-[#FFD700]/40 hover:-translate-y-1 transition-all"
              >
                <div className="w-11 h-11 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-center justify-center">
                  <p.icon className="h-5 w-5 text-[#FFD700]" />
                </div>
                <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-gray-500">
                  Pillar {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-1 font-display text-xl md:text-2xl tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-gray-400 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSFORMATION */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionLabel>Before → After</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
              Your Results Change When Your{" "}
              <span className="text-[#FFD700]">Approach Changes.</span>
            </h2>
            <p className="mt-5 text-gray-300 text-base md:text-lg">
              Here's how students typically transform within the mentorship.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-xl overflow-hidden border border-white/10">
              <div className="grid grid-cols-2 text-[10px] md:text-xs uppercase tracking-[0.22em] bg-white/[0.03]">
                <div className="p-4 text-[#FF3B30] border-r border-white/10 font-semibold">
                  Before
                </div>
                <div className="p-4 text-[#FFD700] font-semibold">After</div>
              </div>
              {TRANSFORMATION.map(([b, a], i) => (
                <div
                  key={i}
                  className="grid grid-cols-2 border-t border-white/10 text-sm md:text-base"
                >
                  <div className="p-4 md:p-5 border-r border-white/10 flex items-start gap-2 text-gray-300">
                    <XCircle className="h-4 w-4 text-[#FF3B30] shrink-0 mt-1" />
                    <span>{b}</span>
                  </div>
                  <div className="p-4 md:p-5 flex items-start gap-2 text-white">
                    <CheckCircle2 className="h-4 w-4 text-[#FFD700] shrink-0 mt-1" />
                    <span>{a}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>Qualify Yourself</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-3xl leading-[1.1]">
            This Mentorship Is For{" "}
            <span className="text-[#FFD700]">Serious Aspirants Only.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {AUDIENCE.map((a, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-xl p-7 bg-white/[0.02] flex gap-5 hover:border-[#FFD700]/40 transition-colors"
              >
                <div className="shrink-0 w-10 h-10 rounded-full bg-[#FFD700] text-black font-display font-bold flex items-center justify-center">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight">{a.title}</h3>
                  <p className="mt-1 text-gray-400 text-sm md:text-base">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <CtaPrimary
              onClick={() => openForm("qualify")}
              testId="qualify-apply-button"
            >
              I'm A Serious Aspirant — Apply
            </CtaPrimary>
          </div>
        </div>
      </section>

      {/* RESULTS / EXPERIENCE IMAGE */}
      <section id="results" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionLabel>12+ Years of Results</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
              The Focus Isn't Just Marks.
              <br />
              It's Building{" "}
              <span className="text-[#FFD700]">Mathematical Thinkers.</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Problem Solving Speed",
                "Mathematical Confidence",
                "Mock Test Performance",
                "Conceptual Understanding",
                "Exam Execution Skills",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-4 text-base md:text-lg">
                  <span className="w-8 h-8 rounded-md border border-[#FFD700]/40 text-[#FFD700] flex items-center justify-center text-xs font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-gray-200">{t}</span>
                  <ChevronRight className="ml-auto h-5 w-5 text-gray-600" />
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#141414]">
              <img
                src={PROMO_POSTER}
                alt="Maths Mama Edtech — IIT JEE Mathematics mentorship, pricing and free demo"
                className="w-full h-auto max-h-[640px] object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>Student Experience</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-3xl leading-[1.1]">
            What Students Actually{" "}
            <span className="text-[#FFD700]">Say After Joining.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-xl p-7 bg-white/[0.03] backdrop-blur hover:border-[#FFD700]/40 transition-colors"
              >
                <Quote className="h-6 w-6 text-[#FFD700]" />
                <p className="mt-4 text-base md:text-lg text-gray-200 leading-relaxed">
                  "{t.quote}"
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FFD700] text-black font-display font-semibold flex items-center justify-center">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YOUTUBE MASTERCLASS SECTION */}
      <section id="youtube" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>Free Content on YouTube</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-4xl leading-[1.1]">
            Watch Suraj Sir{" "}
            <span className="text-[#FFD700]">Teach on YouTube — Free.</span>
          </h2>
          <p className="mt-5 text-gray-300 text-base md:text-lg max-w-2xl">
            Bhagao Maths Ka Bhoot! Subscribe to @MATHSMAMASURAJSIR for free JEE Maths
            strategies, PYQ solving, exam fear management, and the storytelling approach to Mathematics.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                title: "JEE Mains 2026 Strategy",
                desc: "Complete Maths strategy for January session — topic priorities, time management and scoring plan.",
                tag: "Strategy",
              },
              {
                title: "Crack JEE Advanced Series",
                desc: "Step-by-step solving of Advanced PYQs with multiple methods — learn how rankers actually think.",
                tag: "Advanced",
              },
              {
                title: "Exam Fear Management",
                desc: "Live sessions on countering exam fear, nervousness and pressure handling during 3-hour tests.",
                tag: "Mindset",
              },
              {
                title: "PYQ Masterclass",
                desc: "Topic-wise JEE Mains & Advanced Previous Year Questions solved with shortcuts and patterns.",
                tag: "PYQs",
              },
              {
                title: "Storytelling Maths",
                desc: "Understand WHY behind formulas — Trigonometry, Calculus, Algebra explained through real-life stories.",
                tag: "Concepts",
              },
              {
                title: "Daily Shorts & Tips",
                desc: "Quick tricks, common mistake warnings, and 'how to think' approaches for JEE problems.",
                tag: "Quick Tips",
              },
            ].map((v, i) => (
              <a
                key={i}
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-white/10 rounded-xl p-6 bg-white/[0.02] hover:border-red-500/40 hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-red-400 font-semibold">
                    {v.tag}
                  </span>
                  <Play className="h-4 w-4 text-red-400 group-hover:text-red-300" />
                </div>
                <h3 className="mt-3 font-display text-lg md:text-xl tracking-tight group-hover:text-red-300 transition-colors">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </a>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3 items-center">
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-subscribe-button"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wider text-sm transition-colors"
            >
              <Youtube className="h-5 w-5" />
              Subscribe on YouTube
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-button"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:opacity-90 text-white font-semibold uppercase tracking-wider text-sm transition-opacity"
            >
              <Instagram className="h-5 w-5" />
              Follow on Instagram
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              href={TOPMATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="free-demo-button"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-md border border-[#FFD700]/40 text-[#FFD700] font-semibold uppercase tracking-wider text-sm hover:bg-[#FFD700]/10 transition-colors"
            >
              <BookOpen className="h-4 w-4" />
              Book Free Demo Class
            </a>
          </div>
        </div>
      </section>

      {/* WHY STUDENTS JOIN */}
      <section className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionLabel>Why They Choose This</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] max-w-3xl leading-[1.1]">
            Because Serious Aspirants Want{" "}
            <span className="text-[#FFD700]">Real Improvement — Not Just Lectures.</span>
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              "Personal Attention",
              "Real Guidance",
              "Faster Improvement",
              "Structured Prep",
              "Ranker Thinking",
              "Clarity Over Chaos",
            ].map((t, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-lg p-5 bg-white/[0.02] text-center"
              >
                <Zap className="h-5 w-5 text-[#FFD700] mx-auto" />
                <div className="mt-3 text-sm font-medium">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-white/10"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left py-5 text-base md:text-lg font-medium hover:no-underline hover:text-[#FFD700]"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 text-sm md:text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CLOSE */}
      <section className="relative py-24 md:py-32 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-[11px] uppercase tracking-[0.22em] font-semibold mb-7">
            <Timer className="h-3.5 w-3.5" />
            Admissions Closing Soon
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.05]">
            You Don't Need More Lectures.
            <br />
            You Need A{" "}
            <span className="text-[#FFD700]">Plan, A System, A Mentor.</span>
          </h2>
          <p className="mt-6 text-gray-300 text-base md:text-lg">
            This could be the turning point in your JEE preparation. Apply now — seats are
            limited to serious aspirants only.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <CtaPrimary
              onClick={() => openForm("final")}
              testId="final-apply-button"
            >
              Apply for Mentorship Now
            </CtaPrimary>
            <CtaGhost href={WA_LINK} testId="final-whatsapp-button">
              Message on WhatsApp
            </CtaGhost>
          </div>
          <div className="mt-6 text-xs text-gray-500 uppercase tracking-[0.22em]">
            Learn Directly From Suraj Kumar Singh • Kota Expert • 12+ Years Experience
          </div>
        </div>
      </section>

      {/* BIG TYPE FOOTER */}
      <section className="relative overflow-hidden pt-16 md:pt-24 pb-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="font-display font-semibold tracking-[-0.05em] leading-[0.85] text-[16vw] md:text-[12vw] text-white/[0.07] select-none pointer-events-none">
            MATHS MAMA.
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-md bg-[#FFD700] flex items-center justify-center text-black font-display font-bold">
                  S
                </div>
                <div>
                  <div className="font-display text-base tracking-tight">Maths Mama Edtech</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Bhagao Maths Ka Bhoot!</div>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Learn Maths by Storytelling Method and Real Life Based Examples.
                Founded by Suraj Kumar Singh — Kota Expert with 12+ years of JEE Maths mastery.
              </p>
              <SocialLinks className="mt-5" />
            </div>

            {/* Quick Links */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold mb-5">Quick Links</div>
              <div className="space-y-3 text-sm text-gray-400">
                <a href="#mentorship" className="block hover:text-white transition-colors">Mentorship Programs</a>
                <a href="#learn" className="block hover:text-white transition-colors">Curriculum & Pillars</a>
                <a href="#results" className="block hover:text-white transition-colors">Results & Experience</a>
                <a href="#youtube" className="block hover:text-white transition-colors">Free YouTube Content</a>
                <a href="#faq" className="block hover:text-white transition-colors">FAQ</a>
                <a href={TOPMATE_URL} target="_blank" rel="noopener noreferrer" className="block hover:text-[#FFD700] text-[#FFD700]/70 transition-colors">Book Free Demo Class →</a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold mb-5">Contact</div>
              <div className="space-y-4 text-sm text-gray-400">
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors">
                  <Phone className="h-4 w-4 text-green-400 shrink-0" />
                  +91 7448550397 (WhatsApp)
                </a>
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 hover:text-white transition-colors">
                  <Mail className="h-4 w-4 text-[#FFD700] shrink-0" />
                  {EMAIL}
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-gray-500 shrink-0 mt-0.5" />
                  <span>J.P. Nagar Phase 8, Bengaluru 560076, Karnataka, India</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-600 mt-2">
                  Mon–Sat • 6:00 AM – 9:00 PM
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 mt-10 border-t border-white/5">
            <div className="text-xs text-gray-500">
              © {new Date().getFullYear()} Maths Mama Edtech — Suraj Kumar Singh. All rights reserved.
            </div>
            <div className="flex items-center gap-5 text-xs uppercase tracking-[0.2em] text-gray-500">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
              <a href="#" className="hover:text-white">Refund</a>
              <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white">Main Website</a>
              <a href="/admin/login" className="hover:text-white">Admin</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#0f0f0f]/95 backdrop-blur-md border-t border-white/10 p-3 flex gap-2"
        data-testid="sticky-mobile-cta"
      >
        <Button
          onClick={() => openForm("mobile-sticky")}
          data-testid="mobile-sticky-apply-button"
          className="flex-1 bg-[#FFD700] hover:bg-[#E6C200] text-black font-semibold uppercase tracking-wider h-12"
        >
          Apply Now
        </Button>
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-sticky-whatsapp"
          className="w-12 h-12 rounded-md bg-green-500 flex items-center justify-center shrink-0"
        >
          <MessageCircle className="h-5 w-5 text-white" />
        </a>
      </div>

      {/* Floating WhatsApp (desktop) */}
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp"
        className="hidden md:flex fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-green-500 items-center justify-center shadow-[0_0_24px_rgba(34,197,94,0.45)] hover:scale-110 transition-transform"
        aria-label="WhatsApp"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </a>

      {/* Application dialog */}
      <ApplicationForm open={formOpen} onOpenChange={setFormOpen} source={source} />
    </div>
  );
}
